import { motivoDeCorreoDeAgenciaInvalido, motivoDeDescripcionDeAgenciaInvalida } from './programa'

/**
 * La ficha de una agencia socia y el directorio público (CLAUDE.md §30), como el de
 * Kommo: quién es, dónde está, qué hace y en qué idiomas. Las listas son **cerradas**
 * y viven aquí porque las leen el núcleo (que valida), el panel (que pregunta) y la
 * web comercial (que filtra): un servicio que solo existiera en uno de los tres sería
 * un filtro que no encuentra nada.
 *
 * Los valores son nombres sin tildes ni símbolos: acaban en `jsonb` y en direcciones.
 */
type Etiqueta = { es: string; en: string }

export const SERVICIOS_DE_AGENCIA = {
  'diseno-web': { es: 'Diseño web', en: 'Web design' },
  'tiendas-virtuales': { es: 'Tiendas virtuales', en: 'Online stores' },
  seo: { es: 'SEO', en: 'SEO' },
  'marketing-digital': { es: 'Marketing digital', en: 'Digital marketing' },
  'redes-sociales': { es: 'Redes sociales', en: 'Social media' },
  'publicidad-pagada': { es: 'Publicidad pagada', en: 'Paid ads' },
  branding: { es: 'Branding e identidad', en: 'Branding' },
  contenidos: { es: 'Contenidos y redacción', en: 'Content and copywriting' },
  'foto-video': { es: 'Foto y vídeo', en: 'Photo and video' },
  'desarrollo-a-medida': { es: 'Desarrollo a medida', en: 'Custom development' },
  mantenimiento: { es: 'Mantenimiento web', en: 'Website maintenance' },
  consultoria: { es: 'Consultoría', en: 'Consulting' },
} as const satisfies Record<string, Etiqueta>

export const IDIOMAS_DE_AGENCIA = {
  es: { es: 'Español', en: 'Spanish' },
  en: { es: 'Inglés', en: 'English' },
  pt: { es: 'Portugués', en: 'Portuguese' },
  fr: { es: 'Francés', en: 'French' },
  de: { es: 'Alemán', en: 'German' },
  it: { es: 'Italiano', en: 'Italian' },
} as const satisfies Record<string, Etiqueta>

/** Donde está la sede. Los de habla hispana primero, que es el mercado. */
export const PAISES_DE_AGENCIA = {
  PE: { es: 'Perú', en: 'Peru' },
  MX: { es: 'México', en: 'Mexico' },
  CO: { es: 'Colombia', en: 'Colombia' },
  AR: { es: 'Argentina', en: 'Argentina' },
  CL: { es: 'Chile', en: 'Chile' },
  EC: { es: 'Ecuador', en: 'Ecuador' },
  BO: { es: 'Bolivia', en: 'Bolivia' },
  UY: { es: 'Uruguay', en: 'Uruguay' },
  PY: { es: 'Paraguay', en: 'Paraguay' },
  VE: { es: 'Venezuela', en: 'Venezuela' },
  CR: { es: 'Costa Rica', en: 'Costa Rica' },
  PA: { es: 'Panamá', en: 'Panama' },
  GT: { es: 'Guatemala', en: 'Guatemala' },
  SV: { es: 'El Salvador', en: 'El Salvador' },
  HN: { es: 'Honduras', en: 'Honduras' },
  NI: { es: 'Nicaragua', en: 'Nicaragua' },
  DO: { es: 'República Dominicana', en: 'Dominican Republic' },
  PR: { es: 'Puerto Rico', en: 'Puerto Rico' },
  ES: { es: 'España', en: 'Spain' },
  US: { es: 'Estados Unidos', en: 'United States' },
  BR: { es: 'Brasil', en: 'Brazil' },
} as const satisfies Record<string, Etiqueta>

export type ServicioDeAgencia = keyof typeof SERVICIOS_DE_AGENCIA
export type IdiomaDeAgencia = keyof typeof IDIOMAS_DE_AGENCIA
export type PaisDeAgencia = keyof typeof PAISES_DE_AGENCIA

/** Lo que el socio escribe en su ficha. */
export type FichaDeAgencia = {
  empresa: string
  web: string
  correo: string
  telefono: string
  descripcion: string
  ciudad: string
  pais: PaisDeAgencia
  servicios: ServicioDeAgencia[]
  idiomas: IdiomaDeAgencia[]
  /** Si quiere salir en el directorio público. */
  directorio: boolean
  /** Si el directorio enseña su correo y su teléfono. */
  contactoPublico: boolean
}

/** Cuántos servicios puede marcar una agencia: los que de verdad son lo suyo. */
export const MAXIMO_DE_SERVICIOS_DE_AGENCIA = 8

/** Un teléfono con prefijo o sin él: dígitos, espacios, guiones y un «+» delante. */
export const RE_TELEFONO = /^\+?[0-9][0-9 \-]{5,19}$/

const texto = (valor: unknown, maximo: number) => (typeof valor === 'string' ? valor.trim().replace(/\s+/g, ' ').slice(0, maximo) : '')

const lista = <T extends string>(valor: unknown, validos: Record<T, unknown>, maximo: number): T[] => {
  if (!Array.isArray(valor)) return []
  const vistos = new Set<T>()
  for (const v of valor) if (typeof v === 'string' && Object.hasOwn(validos, v)) vistos.add(v as T)
  return [...vistos].slice(0, maximo)
}

/** La web, siempre con `https://` delante. */
export const normalizarWeb = (web: string): string => {
  const limpia = web.trim()
  if (!limpia) return ''
  return /^https?:\/\//i.test(limpia) ? limpia.replace(/^http:\/\//i, 'https://') : `https://${limpia}`
}

/**
 * Lee una ficha de lo que llega por la API: recorta, descarta lo que no está en las
 * listas y devuelve el primer problema en llano con su campo, o la ficha limpia. La
 * descripción larga (200 a 1000) y el correo del dominio de la web salen de
 * `programa.ts`, que son las mismas reglas de la solicitud.
 */
export const revisarFichaDeAgencia = (entrada: Record<string, unknown>): { ok: true; ficha: FichaDeAgencia } | { ok: false; campo: keyof FichaDeAgencia; mensaje: string } => {
  const empresa = texto(entrada.empresa, 120)
  const web = normalizarWeb(texto(entrada.web, 200))
  const correo = texto(entrada.correo, 200).toLowerCase()
  const telefono = texto(entrada.telefono, 24)
  const descripcion = typeof entrada.descripcion === 'string' ? entrada.descripcion.trim().slice(0, 2000) : ''
  const ciudad = texto(entrada.ciudad, 80)
  const pais = typeof entrada.pais === 'string' ? entrada.pais.trim().toUpperCase() : ''
  const servicios = lista(entrada.servicios, SERVICIOS_DE_AGENCIA, MAXIMO_DE_SERVICIOS_DE_AGENCIA)
  const idiomas = lista(entrada.idiomas, IDIOMAS_DE_AGENCIA, 6)

  if (empresa.length < 2) return { ok: false, campo: 'empresa', mensaje: 'Di cómo se llama tu agencia.' }
  let dominio = ''
  try {
    dominio = new URL(web).hostname
  } catch {
    /* abajo */
  }
  if (!dominio.includes('.')) return { ok: false, campo: 'web', mensaje: 'Escribe la dirección de tu web, por ejemplo https://tuagencia.pe.' }
  const malCorreo = motivoDeCorreoDeAgenciaInvalido(correo, web)
  if (malCorreo) return { ok: false, campo: 'correo', mensaje: malCorreo }
  if (!RE_TELEFONO.test(telefono)) return { ok: false, campo: 'telefono', mensaje: 'Escribe un teléfono de contacto, con el prefijo del país: +51 987 654 321.' }
  if (ciudad.length < 2) return { ok: false, campo: 'ciudad', mensaje: 'Di en qué ciudad está tu agencia.' }
  if (!Object.hasOwn(PAISES_DE_AGENCIA, pais)) return { ok: false, campo: 'pais', mensaje: 'Elige el país de tu agencia.' }
  if (servicios.length === 0) return { ok: false, campo: 'servicios', mensaje: 'Marca al menos un servicio: es por lo que te van a buscar.' }
  if (idiomas.length === 0) return { ok: false, campo: 'idiomas', mensaje: 'Marca al menos un idioma en el que atiendes.' }
  const malDescripcion = motivoDeDescripcionDeAgenciaInvalida(descripcion)
  if (malDescripcion) return { ok: false, campo: 'descripcion', mensaje: malDescripcion }

  return {
    ok: true,
    ficha: {
      empresa,
      web,
      correo,
      telefono,
      descripcion,
      ciudad,
      pais: pais as PaisDeAgencia,
      servicios,
      idiomas,
      directorio: entrada.directorio !== false,
      contactoPublico: entrada.contactoPublico === true,
    },
  }
}

/** «Monstruo Creativo S.A.C.» → `monstruo-creativo`: la dirección de su ficha en el directorio. */
export const slugDeAgencia = (empresa: string): string =>
  empresa
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\b(s\.?\s?a\.?\s?c\.?|s\.?\s?r\.?\s?l\.?|e\.?\s?i\.?\s?r\.?\s?l\.?|s\.?\s?a\.?|s\.?\s?l\.?|l\.?\s?l\.?\s?c\.?|inc\.?|ltd\.?)$/i, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/, '') || 'agencia'

/** Un enlace de WhatsApp a partir del teléfono: solo dígitos. */
export const whatsappDe = (telefono: string): null | string => {
  const digitos = telefono.replace(/\D/g, '')
  return digitos.length >= 8 ? `https://wa.me/${digitos}` : null
}

/** Lo que el directorio público enseña de una agencia. Nunca su código, sus clientes ni lo que ha cobrado. */
export type AgenciaDelDirectorio = {
  slug: string
  empresa: string
  web: string
  descripcion: string
  ciudad: string
  pais: PaisDeAgencia
  servicios: ServicioDeAgencia[]
  idiomas: IdiomaDeAgencia[]
  logo: null | string
  /** Solo si la agencia lo quiere público. */
  correo: null | string
  telefono: null | string
  /** El año en que la aprobó Wixy. */
  socioDesde: number
}
